import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { BRANDING } from '@/features/marketing/content/branding';
import { marketingMeta } from '@/features/marketing/seo';
import { useStorefrontMeta } from '@/features/storefront/useStorefrontMeta';

/**
 * The current marketing page's title, description and canonical address.
 *
 * The canonical link drops the query string, so `/pricing?ref=abc` and
 * `/pricing?cycle=yearly` are one page to a search engine rather than several
 * copies of it.
 */
export function useMarketingMeta() {
  const { pathname } = useLocation();
  const meta = marketingMeta(pathname);
  const isHome = pathname === '/' || pathname === '';
  useStorefrontMeta(
    isHome
      ? { title: BRANDING.homeTitle, description: BRANDING.homeDescription }
      : { title: meta?.title, description: meta?.description, storeName: BRANDING.name },
  );

  // The owner's favicon, when they set one, replaces the bundled icons.
  const favicon = BRANDING.faviconSrc;
  useEffect(() => {
    if (!favicon) return;
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    const previous = links.map((link) => link.href);
    for (const link of links) link.href = favicon;
    return () => links.forEach((link, index) => (link.href = previous[index]));
  }, [favicon]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = `${window.location.origin}${pathname}`;
    document.head.appendChild(link);
    return () => link.remove();
  }, [pathname]);
}
